import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import {
  CHAT_SETTINGS,
  CHAT_WIDGET_POSITIONS,
  CHAT_WIDGET_THEMES,
  CHAT_SETTINGS_DEFAULTS,
  CHAT_SECRET_SETTINGS,
} from '@limo/shared'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Eye, EyeOff, MessageSquare, Phone, Users, Sparkles } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

// Schema for form validation
const aiAssistantSchema = z.object({
  // Core settings
  enabled: z.boolean(),
  groq_api_key: z.string().optional(),
  greeting_message: z.string().max(500, 'Greeting message should be under 500 characters').optional(),
  system_prompt: z.string().max(2000, 'System prompt should be under 2000 characters').optional(),

  // Widget appearance
  widget_position: z.enum(['bottom-right', 'bottom-left']),
  widget_theme: z.enum(['light', 'dark', 'auto']),
  primary_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color').optional(),

  // Callback settings
  callback_enabled: z.boolean(),
  callback_email: z.string().email('Must be a valid email').optional().or(z.literal('')),

  // Live handoff settings
  live_handoff_enabled: z.boolean(),
  live_handoff_email: z.string().email('Must be a valid email').optional().or(z.literal('')),
  live_handoff_phone: z.string().optional(),

  // Proactive engagement
  proactive_delay: z.coerce.number().min(0, 'Delay must be 0 or greater').max(300, 'Delay must be under 5 minutes'),
  proactive_message: z.string().max(200, 'Proactive message should be under 200 characters').optional(),

  // Limits
  max_messages: z.coerce.number().min(10, 'Minimum 10 messages').max(200, 'Maximum 200 messages'),
})

type AIAssistantFormData = z.infer<typeof aiAssistantSchema>

export function AIAssistantPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [showApiKey, setShowApiKey] = useState(false)

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Fetch account settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  // Form with defaults
  const form = useForm<AIAssistantFormData>({
    resolver: zodResolver(aiAssistantSchema),
    defaultValues: {
      enabled: false,
      groq_api_key: '',
      greeting_message: CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.GREETING_MESSAGE],
      system_prompt: '',
      widget_position: 'bottom-right',
      widget_theme: 'auto',
      primary_color: CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.PRIMARY_COLOR],
      callback_enabled: false,
      callback_email: '',
      live_handoff_enabled: false,
      live_handoff_email: '',
      live_handoff_phone: '',
      proactive_delay: 0,
      proactive_message: CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.PROACTIVE_MESSAGE],
      max_messages: 50,
    },
  })

  // Update form when settings are loaded
  useEffect(() => {
    if (settings) {
      form.reset({
        enabled: settings[CHAT_SETTINGS.ENABLED] === 'true',
        groq_api_key: settings[CHAT_SETTINGS.GROQ_API_KEY] || '',
        greeting_message: settings[CHAT_SETTINGS.GREETING_MESSAGE] || CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.GREETING_MESSAGE],
        system_prompt: settings[CHAT_SETTINGS.SYSTEM_PROMPT] || '',
        widget_position: (settings[CHAT_SETTINGS.WIDGET_POSITION] as 'bottom-right' | 'bottom-left') || 'bottom-right',
        widget_theme: (settings[CHAT_SETTINGS.WIDGET_THEME] as 'light' | 'dark' | 'auto') || 'auto',
        primary_color: settings[CHAT_SETTINGS.PRIMARY_COLOR] || CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.PRIMARY_COLOR],
        callback_enabled: settings[CHAT_SETTINGS.CALLBACK_ENABLED] === 'true',
        callback_email: settings[CHAT_SETTINGS.CALLBACK_EMAIL] || '',
        live_handoff_enabled: settings[CHAT_SETTINGS.LIVE_HANDOFF_ENABLED] === 'true',
        live_handoff_email: settings[CHAT_SETTINGS.LIVE_HANDOFF_EMAIL] || '',
        live_handoff_phone: settings[CHAT_SETTINGS.LIVE_HANDOFF_PHONE] || '',
        proactive_delay: parseInt(settings[CHAT_SETTINGS.PROACTIVE_TRIGGER_DELAY] || '0', 10),
        proactive_message: settings[CHAT_SETTINGS.PROACTIVE_MESSAGE] || CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.PROACTIVE_MESSAGE],
        max_messages: parseInt(settings[CHAT_SETTINGS.MAX_MESSAGES_PER_CONVERSATION] || '50', 10),
      })
    }
  }, [settings, form])

  // Mutation to save settings
  const mutation = useMutation({
    mutationFn: async (data: AIAssistantFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const settingsToUpsert = [
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.ENABLED,
          value: data.enabled ? 'true' : 'false',
          description: 'Enable/disable AI chat widget',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.GROQ_API_KEY,
          value: data.groq_api_key || null,
          description: 'Groq API key for AI responses',
          is_secret: true,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.GREETING_MESSAGE,
          value: data.greeting_message || null,
          description: 'Initial greeting message',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.SYSTEM_PROMPT,
          value: data.system_prompt || null,
          description: 'Custom AI personality/instructions',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.WIDGET_POSITION,
          value: data.widget_position,
          description: 'Chat widget position',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.WIDGET_THEME,
          value: data.widget_theme,
          description: 'Chat widget color theme',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.PRIMARY_COLOR,
          value: data.primary_color || null,
          description: 'Primary accent color',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.CALLBACK_ENABLED,
          value: data.callback_enabled ? 'true' : 'false',
          description: 'Enable callback requests',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.CALLBACK_EMAIL,
          value: data.callback_email || null,
          description: 'Email for callback notifications',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.LIVE_HANDOFF_ENABLED,
          value: data.live_handoff_enabled ? 'true' : 'false',
          description: 'Enable live agent handoff',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.LIVE_HANDOFF_EMAIL,
          value: data.live_handoff_email || null,
          description: 'Email for handoff notifications',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.LIVE_HANDOFF_PHONE,
          value: data.live_handoff_phone || null,
          description: 'Phone for live handoff',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.PROACTIVE_TRIGGER_DELAY,
          value: String(data.proactive_delay),
          description: 'Proactive message delay in seconds',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.PROACTIVE_MESSAGE,
          value: data.proactive_message || null,
          description: 'Proactive engagement message',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CHAT_SETTINGS.MAX_MESSAGES_PER_CONVERSATION,
          value: String(data.max_messages),
          description: 'Maximum messages per conversation',
          is_secret: false,
        },
      ]

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'AI Assistant settings saved',
        description: 'Your chat widget settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const watchEnabled = form.watch('enabled')
  const watchCallbackEnabled = form.watch('callback_enabled')
  const watchHandoffEnabled = form.watch('live_handoff_enabled')
  const watchProactiveDelay = form.watch('proactive_delay')

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-24" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
          className="space-y-6"
        >
          {/* Main Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                <CardTitle>AI Chat Assistant</CardTitle>
              </div>
              <CardDescription>
                Enable an AI-powered chat widget to help customers with booking inquiries
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Enable Chat Widget</FormLabel>
                      <FormDescription>
                        Show the AI chat widget on your public website
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {watchEnabled && (
                <>
                  <FormField
                    control={form.control}
                    name="groq_api_key"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Groq API Key</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showApiKey ? 'text' : 'password'}
                              placeholder="gsk_..."
                              {...field}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3"
                              onClick={() => setShowApiKey(!showApiKey)}
                            >
                              {showApiKey ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Get your API key from{' '}
                          <a
                            href="https://console.groq.com/keys"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline"
                          >
                            console.groq.com
                          </a>
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="greeting_message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Greeting Message</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Hi there! How can I help you today?"
                            className="resize-none"
                            maxLength={500}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Initial message shown when the chat opens ({field.value?.length || 0}/500 characters)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="system_prompt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>AI Personality & Instructions</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="You are a helpful assistant for a luxury limousine service. Be professional, friendly, and focus on helping customers book rides..."
                            className="min-h-[120px] resize-none"
                            maxLength={2000}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Custom instructions to shape the AI's behavior ({field.value?.length || 0}/2000 characters)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </CardContent>
          </Card>

          {/* Widget Appearance */}
          {watchEnabled && (
            <Card>
              <CardHeader>
                <CardTitle>Widget Appearance</CardTitle>
                <CardDescription>
                  Customize how the chat widget looks on your website
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="widget_position"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Widget Position</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select position" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="bottom-right">Bottom Right</SelectItem>
                            <SelectItem value="bottom-left">Bottom Left</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="widget_theme"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Color Theme</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select theme" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="light">Light</SelectItem>
                            <SelectItem value="dark">Dark</SelectItem>
                            <SelectItem value="auto">Auto (System)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="primary_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Accent Color</FormLabel>
                      <FormControl>
                        <div className="flex items-center gap-3">
                          <Input
                            type="color"
                            className="h-10 w-14 cursor-pointer p-1"
                            {...field}
                          />
                          <Input
                            type="text"
                            placeholder="#3B82F6"
                            className="flex-1"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        Primary color for buttons and accents
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {/* Callback Settings */}
          {watchEnabled && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  <CardTitle>Callback Requests</CardTitle>
                </div>
                <CardDescription>
                  Allow customers to request a phone callback from the chat
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <FormField
                  control={form.control}
                  name="callback_enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Enable Callback Requests</FormLabel>
                        <FormDescription>
                          Customers can request you call them back
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {watchCallbackEnabled && (
                  <FormField
                    control={form.control}
                    name="callback_email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notification Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="callbacks@yourcompany.com"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Email to receive callback request notifications
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>
          )}

          {/* Live Handoff Settings */}
          {watchEnabled && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  <CardTitle>Live Agent Handoff</CardTitle>
                </div>
                <CardDescription>
                  Allow customers to connect with a live agent when AI can't help
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <FormField
                  control={form.control}
                  name="live_handoff_enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Enable Live Handoff</FormLabel>
                        <FormDescription>
                          Show option to connect with a live agent
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {watchHandoffEnabled && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="live_handoff_email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Handoff Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="support@yourcompany.com"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Notify when handoff is requested
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="live_handoff_phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Phone</FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              placeholder="(555) 123-4567"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Phone shown to customers
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Proactive Engagement */}
          {watchEnabled && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  <CardTitle>Proactive Engagement</CardTitle>
                </div>
                <CardDescription>
                  Automatically prompt visitors to chat after a delay
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <FormField
                  control={form.control}
                  name="proactive_delay"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Trigger Delay (seconds)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          max={300}
                          placeholder="0"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        {field.value === 0 || !field.value
                          ? 'Proactive messages disabled (set to 0)'
                          : `Show proactive message after ${field.value} seconds`}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {watchProactiveDelay > 0 && (
                  <FormField
                    control={form.control}
                    name="proactive_message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Proactive Message</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Need help finding the perfect ride?"
                            className="resize-none"
                            maxLength={200}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Message shown to prompt visitors ({field.value?.length || 0}/200 characters)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>
          )}

          {/* Limits */}
          {watchEnabled && (
            <Card>
              <CardHeader>
                <CardTitle>Conversation Limits</CardTitle>
                <CardDescription>
                  Set limits to manage API usage and costs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="max_messages"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max Messages Per Conversation</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={10}
                          max={200}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Maximum messages allowed in a single chat session (10-200)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save Changes
          </Button>
        </form>
      </Form>
    </div>
  )
}
